import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { MSym } from '@storeo/ui';

export type Device = 'desktop' | 'tablet' | 'mobile';

/** Largura lógica de cada device — a loja é renderizada NESSA largura real e o frame
 *  inteiro é escalado (`transform: scale`) pra caber no espaço disponível. Assim o preview
 *  representa o tamanho real reduzido, sem disparar os breakpoints responsivos e "quebrar". */
const LOGICAL_WIDTH: Record<Device, number> = {
  desktop: 1280,
  tablet: 834,
  mobile: 390,
};

/** Moldura de navegador simulado do Studio — réplica de
 *  docs/prototype/lpplat/construtor/code.html (linhas ~290–636): badge flutuante de
 *  personalização ativa, chrome com window dots + address bar white-label, e barra de
 *  status inferior. O conteúdo (`<TemplatePreview/>`) entra como `children`. */
export function BrowserFrame({
  host,
  device,
  children,
}: {
  host: string;
  device: Device;
  children: ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [footprintH, setFootprintH] = useState(0);
  const logical = LOGICAL_WIDTH[device];

  useLayoutEffect(() => {
    const hostEl = hostRef.current;
    const frameEl = frameRef.current;
    if (!hostEl || !frameEl) return;
    const recompute = () => {
      const avail = hostEl.clientWidth;
      const s = Math.min(1, avail / logical);
      setScale(s);
      setFootprintH(frameEl.offsetHeight * s);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(hostEl);
    ro.observe(frameEl);
    return () => ro.disconnect();
  }, [logical]);

  return (
    <div className="flex-1 min-w-0 bg-surface-dim p-space-sm md:p-space-lg flex flex-col items-center overflow-x-hidden relative rounded-2xl">
      <div className="absolute top-space-md right-space-lg z-30 hidden md:flex items-center gap-2 bg-surface-container-high/90 backdrop-blur-md px-space-sm py-1.5 rounded-full shadow-xl">
        <span className="w-2 h-2 rounded-full bg-primary animate-ping" />
        <span className="font-label-sm text-label-sm text-white">
          Editando em Tempo Real:{' '}
          <strong className="text-primary font-bold">Tema Cinema + Botão Emerald</strong>
        </span>
      </div>

      {/* host mede o espaço; reserva a "pegada" já escalada do frame */}
      <div ref={hostRef} className="w-full flex justify-center" style={{ maxWidth: logical }}>
        <div style={{ width: logical * scale, height: footprintH || undefined }}>
          <div
            ref={frameRef}
            className="bg-surface-container-lowest rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ width: logical, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          >
            {/* Browser chrome */}
            <div className="bg-surface-container-low px-space-md py-2.5 flex items-center justify-between gap-space-sm">
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="w-3 h-3 rounded-full bg-[#ef4444]/80" />
                <span className="w-3 h-3 rounded-full bg-[#f59e0b]/80" />
                <span className="w-3 h-3 rounded-full bg-[#10b981]/80" />
              </div>
              <div className="flex-1 max-w-xl mx-auto flex items-center justify-between bg-surface-container-lowest px-space-sm py-1 rounded-lg text-on-surface-variant min-w-0">
                <div className="flex items-center gap-1.5 font-mono text-label-sm truncate">
                  <MSym name="lock" className="text-secondary text-[14px]" fill />
                  <span className="text-secondary font-semibold">https://</span>
                  <span className="text-white font-bold truncate">{host}</span>
                  <span className="text-on-surface-variant/40">/vitrine-exclusiva</span>
                </div>
                <MSym
                  name="refresh"
                  className="text-[16px] text-on-surface-variant hover:text-white cursor-pointer shrink-0"
                />
              </div>
              <div className="hidden sm:flex items-center gap-1 bg-surface-container px-2 py-0.5 rounded-md shrink-0">
                <MSym name="verified" className="text-secondary text-[14px]" />
                <span className="font-label-sm text-[11px] text-on-surface font-semibold">
                  Zero Storeo Branding
                </span>
              </div>
            </div>

            {/* Loja renderizada — largura real, sem clip; o frame inteiro é que escala */}
            <div className="w-full">{children}</div>
          </div>
        </div>
      </div>

      {/* Barra de status inferior */}
      <div
        className="mt-space-sm flex flex-col sm:flex-row items-center justify-between gap-2 text-on-surface-variant font-label-sm text-label-sm px-2"
        style={{ width: logical * scale, maxWidth: '100%' }}
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-secondary" />
          <span>
            Sincronização em tempo real ativada • Latência: <strong>14ms</strong>
          </span>
        </div>
        <div className="flex items-center gap-space-md">
          <span>
            Servidor Edge: <strong>GRU1 (São Paulo)</strong>
          </span>
          <span>
            Zoom: <strong>{Math.round(scale * 100)}%</strong>
          </span>
        </div>
      </div>
    </div>
  );
}
